import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TextField as MUITextField,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { WatchActions } from "../../../state";
import { useSelector, shallowEqual } from "react-redux";
import { useFormikContext } from "formik";
import { useForceUpdate } from "../../../lib";
import { MultiSelectChips } from "./MultiSelectChips";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
// import RefreshIcon from "@material-ui/icons/Refresh"; // TEMP disabled with the "Refresh from calibre" icon — re-enable together. See memory: calibre-rollover-stale-movement.
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Scalar movement fields that participate in the unified linked/detached
// detection. calibre_id is intentionally NOT in this list — it's the official
// link to a calibre record and must always reflect the assigned calibre.
const SCALAR_OVERRIDE_FIELDS = [
  "hz", "vph", "jewels", "components",
  "reserve_hours", "reserve_days", "reserve_notes",
  "in_house", "base_calibre", "movement_notes", "movement_type_id",
  "eol", "eol_notes",
];

// "Linked" means the watch is showing pure calibre_db data with no item-level
// overrides anywhere in the Movement section. "Detached" (the opposite) means
// at least one scalar override, complication row, or certification override
// exists. On first change in detached transition, ensureDetached() snapshots
// every current display value to Formik.
const detectIsLinked = (vals) => {
  if (!vals) return true;
  const hasScalar = SCALAR_OVERRIDE_FIELDS.some(
    (f) => vals[f] !== null && vals[f] !== undefined
  );
  const hasComp = (vals.item_complications?.length ?? 0) > 0;
  const hasCert = (vals.item_certification_ids?.length ?? 0) > 0;
  return !hasScalar && !hasComp && !hasCert;
};

export const Movement = (props) => {
  const selectStyles = {
    fontSize: "12px",
    height: "24px",
    padding: "2px 12px 2px 5px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    backgroundColor: "transparent",
    width: "100%",
  };

  const siteId = useSelector((state) => state.scopes.site_id);
  const { setChildCalibreData } = props;
  let forceUpdate = useForceUpdate();
  const { classes } = props;
  const { values, setFieldValue } = useFormikContext();
  const [checkboxes, setCheckboxes] = useState({});
  const [oldCalibre, setOldCalibre] = useState();
  const [reserve_hours, set_reserve_hours] = useState("");
  const [reserve_notes, set_reserve_notes] = useState("");
  const [certification_ids_state, set_certification_ids_state] = useState([]);
  const [base_calibre_state, set_base_calibre_state] = useState(0);
  const [reserve_days, set_reserve_days] = useState("");
  const [vph, set_vph] = useState("");
  const [components, set_components] = useState("");
  const [jewels, set_jewels] = useState("");
  const [in_house, set_in_house] = useState(false);
  const [hz, set_hz] = useState("");
  const [movement_notes, set_movement_notes] = useState("");
  const [movement_type_override, set_movement_type_override] = useState(null);
  // EOL (quartz only) — calibre-backed like the other scalars
  const [eol_state, set_eol_state] = useState(false);
  const [eol_notes_state, set_eol_notes_state] = useState("");
  // Free-form calibre name — the alternative to a calibre_db link (Magento's
  // calibre_name). Standalone item field, NOT part of the linked/detached model.
  const [calibre_name, set_calibre_name] = useState("");
  const [errorDialog, setErrorDialog] = useState(false);
  const [calibreLoading, setCalibreLoading] = useState(false);
  const [isLinked, setIsLinked] = useState(() => detectIsLinked(values));
  // Tracks the item_id we last populated the no-calibre display path for, so a
  // calibre-less watch fills its fields once per item (and re-fills when the
  // admin navigates to a different watch without a full remount).
  const noCalibreInitedFor = useRef(null);

  // Snapshots every current Movement-section display value into Formik.
  // No-op once detached. Called from every onChange handler in the section, so
  // a single edit (scalar, complication toggle, cert change) "freezes" all the
  // calibre-default values onto the item record in one clean sweep.
  const ensureDetached = () => {
    if (!isLinked) return;
    setFieldValue("hz", hz !== "" ? parseFloat(hz) : null);
    setFieldValue("vph", vph !== "" ? parseInt(vph) : null);
    setFieldValue("jewels", jewels !== "" ? jewels : null);
    setFieldValue("components", components !== "" ? components : null);
    setFieldValue("reserve_hours", reserve_hours !== "" ? parseFloat(reserve_hours) : null);
    setFieldValue("reserve_days", reserve_days !== "" ? parseFloat(reserve_days) : null);
    setFieldValue("reserve_notes", reserve_notes || null);
    setFieldValue("in_house", in_house ? 1 : 0);
    setFieldValue("base_calibre", base_calibre_state?.calibre_id ?? null);
    setFieldValue("movement_notes", movement_notes || null);
    setFieldValue("movement_type_id", movement_type_override);
    setFieldValue("eol", eol_state ? 1 : 0);
    setFieldValue("eol_notes", eol_notes_state || null);
    // Complications: snapshot current checkbox state (calibre defaults if linked).
    const compSnap = Object.entries(checkboxes)
      .filter(([, e]) => e.checked)
      .map(([id, e]) => ({
        complication_id: parseInt(id),
        details: e.detailLines.filter((d) => d.trim()).join("\n") || null,
      }));
    setFieldValue("item_complications", compSnap);
    // Certifications: snapshot current selection.
    setFieldValue("item_certification_ids", certification_ids_state);
    setIsLinked(false);
  };

  const syncItemComplications = (updatedCheckboxes) => {
    const itemComplications = Object.entries(updatedCheckboxes)
      .filter(([, entry]) => entry.checked)
      .map(([id, entry]) => ({
        complication_id: parseInt(id),
        details: entry.detailLines.filter((d) => d.trim()).join("\n") || null,
      }));
    setFieldValue("item_complications", itemComplications);
  };
  const deepCopy = (v) => JSON.parse(JSON.stringify(v));
  const updateCalibre = (value, revert, isInitialLoad = false) => {
    if (value && !revert) {
      setOldCalibre(value);
      setCalibreLoading(true);
      (async function () {
        const data = await WatchActions.getMovementDetails(value.calibre_id);
        console.log("data", data);
        const initialCheckboxes = (data.item_calibre_complications || []).reduce((prev, curr) => {
          const rawDetails = curr.item_calibre_complication_ref?.details || "";
          const detailLines = rawDetails ? rawDetails.split("\n").filter((d) => d.trim()) : [];
          prev[curr.complication_id] = {
            complication: curr.complication,
            checked: true,
            detailLines: detailLines.length ? detailLines : [""],
          };
          return prev;
        }, {});
        const certIds = (data.item_certifications || []).map((c) => c.certification_id);
        const newChildCalibreData = {
          ...deepCopy(data),
          complications: Object.keys(initialCheckboxes).map((id) => parseInt(id)),
          complication_details: Object.entries(initialCheckboxes)
            .map(([id, item]) => ({
              calibre_complication_id: parseInt(id),
              details: item.detailLines.filter((d) => d.trim()).join("\n") || null,
            }))
            .filter((d) => d.details),
          certification_ids: certIds,
        };
        setChildCalibreData(newChildCalibreData);

        // Unified detection: are we currently "linked" to calibre defaults, or
        // "detached" with item-level overrides? Computed once from the loaded
        // Formik values so all three branches (complications, certs, scalars)
        // agree.
        const linked = detectIsLinked(values);

        // Complications: detached → render exactly the item rows; linked →
        // render calibre defaults. On calibre change, always reset to calibre
        // defaults and wipe item-level overrides.
        let activeCheckboxes;
        if (isInitialLoad && !linked) {
          activeCheckboxes = Object.fromEntries(
            (values.item_complications || []).map((c) => {
              const itemDetails = c.details?.trim();
              const detailLines = itemDetails
                ? itemDetails.split("\n").filter((d) => d.trim())
                : [""];
              const calibreEntry = initialCheckboxes[String(c.complication_id)];
              const comp =
                calibreEntry ||
                calibre_complication_list?.find((x) => x.complication_id === c.complication_id);
              return [
                String(c.complication_id),
                {
                  complication: calibreEntry?.complication || comp?.complication || "",
                  checked: true,
                  detailLines: detailLines.length ? detailLines : [""],
                },
              ];
            })
          );
        } else {
          activeCheckboxes = initialCheckboxes;
          if (!isInitialLoad) setFieldValue("item_complications", []);
        }
        setCheckboxes(activeCheckboxes);

        // Certifications: detached → item selection; linked → calibre's certs.
        const activeCertIds =
          isInitialLoad && !linked ? (values.item_certification_ids || []) : certIds;
        if (!isInitialLoad) setFieldValue("item_certification_ids", []);
        set_certification_ids_state(activeCertIds);

        // Scalars: per-field calibre fallback even when detached. An item can be
        // "detached" because of complications/certifications without having any
        // scalar override saved — in that case `values.hz` etc. are null and we
        // still want to display the calibre default. (The all-or-nothing refactor
        // initially returned `itemVal` unconditionally when detached, which left
        // fields blank for those watches; restored the per-field fallback below.)
        const effective = (itemVal, calibreVal) => {
          if (!isInitialLoad) return calibreVal;
          if (linked) return calibreVal;
          return (itemVal !== null && itemVal !== undefined) ? itemVal : calibreVal;
        };

        set_jewels(effective(values.jewels, data.jewels) || "");
        set_components(effective(values.components, data.components) || "");
        set_vph(effective(values.vph, data.vph) || "");
        set_reserve_days(effective(values.reserve_days, data.reserve_days) || "");
        set_reserve_hours(effective(values.reserve_hours, data.reserve_hours) || "");
        set_hz(effective(values.hz, data.vph / 7200) || "");
        set_reserve_notes(effective(values.reserve_notes, data.reserve_notes) || "");
        set_in_house(!!effective(values.in_house, data.in_house));
        set_base_calibre_state(
          calibre_list.find((r) => r.calibre_id === effective(values.base_calibre, data.parent_calibre_id)) || "",
        );
        set_movement_notes(effective(values.movement_notes, data.description) || "");
        set_movement_type_override(effective(values.movement_type_id, data.movement_type_id) ?? null);
        set_eol_state(!!effective(values.eol, data.eol));
        set_eol_notes_state(effective(values.eol_notes, data.eol_notes) || "");

        if (!isInitialLoad) {
          setFieldValue('hz', null);
          setFieldValue('vph', null);
          setFieldValue('jewels', null);
          setFieldValue('components', null);
          setFieldValue('reserve_hours', null);
          setFieldValue('reserve_days', null);
          setFieldValue('reserve_notes', null);
          setFieldValue('in_house', null);
          setFieldValue('base_calibre', null);
          setFieldValue('movement_notes', null);
          setFieldValue('movement_type_id', null);
          setFieldValue('eol', null);
          setFieldValue('eol_notes', null);
          setIsLinked(true);
        } else {
          setIsLinked(linked);
        }
        setCalibreLoading(false);
      })();
    }
    setFieldValue(`calibre_id`, value ? value[`calibre_id`] : "");
    forceUpdate();
  };
  function handleChangeHz(e) {
    ensureDetached();
    const hzVal = e.target.value !== "" ? e.target.value : null;
    const vphVal = hzVal !== null ? Math.round(parseFloat(hzVal) * 7200) : null;
    set_hz(e.target.value);
    set_vph(vphVal ?? "");
    setFieldValue('hz', hzVal !== null ? parseFloat(hzVal) : null);
    setFieldValue('vph', vphVal);
    if (e.target.value % 0.5 !== 0) {
      setErrorDialog(true);
    } else setErrorDialog(false);
  }
  function handleChangeVph(e) {
    ensureDetached();
    const vphVal = e.target.value !== "" ? e.target.value : null;
    const hzVal = vphVal !== null ? parseFloat(vphVal) / 7200 : null;
    set_vph(e.target.value);
    set_hz(hzVal ?? "");
    setFieldValue('vph', vphVal !== null ? parseInt(vphVal) : null);
    setFieldValue('hz', hzVal);
    if (e.target.value % 7200 !== 0) {
      setErrorDialog(true);
    } else setErrorDialog(false);
  }

  const {
    certification: certification_list,
    calibre: calibre_list,
    calibre_complication: calibre_complication_list,
    calibre_movement_type: calibre_movement_type_list,
  } = useSelector((state) => state.watches.watchLists, shallowEqual);

  // Populate the Movement display fields directly from the item's own values,
  // for calibre-LESS watches (no parent calibre to inherit from — the Magento
  // "manual insertion" pattern and the bulk of pt_legacy items). Mirrors the
  // "detached" branch of updateCalibre but sources everything from `values.*`.
  // Without this the whole section renders blank for the ~94% of the catalog
  // that isn't linked to a calibre_db record, even though `item` carries the
  // data (and the storefront already shows it).
  const populateFromItem = () => {
    // Mirror the calibre path: derive hz from vph when hz itself isn't stored.
    set_hz(values.hz != null ? values.hz : (values.vph != null ? values.vph / 7200 : ""));
    set_vph(values.vph ?? "");
    set_jewels(values.jewels ?? "");
    set_components(values.components ?? "");
    set_reserve_hours(values.reserve_hours ?? "");
    set_reserve_days(values.reserve_days ?? "");
    set_reserve_notes(values.reserve_notes ?? "");
    set_in_house(!!values.in_house);
    set_movement_notes(values.movement_notes ?? "");
    set_movement_type_override(values.movement_type_id ?? null);
    set_eol_state(!!values.eol);
    set_eol_notes_state(values.eol_notes ?? "");
    set_base_calibre_state(
      calibre_list?.find((r) => r.calibre_id === values.base_calibre) || "",
    );
    set_certification_ids_state(values.item_certification_ids ?? []);
    const cb = {};
    (values.item_complications || []).forEach((c) => {
      const comp = calibre_complication_list?.find(
        (x) => x.complication_id === c.complication_id,
      );
      const lines = c.details?.trim()
        ? c.details.split("\n").filter((d) => d.trim())
        : [""];
      cb[c.complication_id] = {
        complication: comp?.complication || "",
        checked: true,
        detailLines: lines.length ? lines : [""],
      };
    });
    setCheckboxes(cb);
    // Calibre-less = the item is the sole source of truth → detached.
    setIsLinked(false);
  };

  useEffect(() => {
    // Auto-fetch calibre defaults on initial load.
    // Both `calibre_list` (from Redux) and `values.calibre_id` (from Formik's
    // API-driven initialValues) need to be present for the find to succeed —
    // and they don't necessarily arrive in the same render. Without `values.calibre_id`
    // in the dep array, this effect missed the case where calibre_list was
    // already loaded but the form's calibre_id arrived a tick later → the
    // movement fields stayed blank until the admin manually toggled the dropdown.
    // The oldCalibre check is replaced with a calibre_id comparison so a re-fire
    // on the same calibre after Formik reinit (e.g. after Update) is a no-op,
    // while a calibre swap correctly re-runs.
    if (!calibre_list) return;
    if (
      values.calibre_id &&
      calibre_list.find((x) => x?.calibre_id === values.calibre_id) &&
      (!oldCalibre || oldCalibre.calibre_id !== values.calibre_id)
    ) {
      updateCalibre(
        calibre_list.find((x) => x?.calibre_id === values.calibre_id),
        undefined,
        true,
      );
    } else if (
      // Calibre-less watch: show the item's own movement data. Gate on
      // values.item_id (the form has loaded) and populate once per item.
      !values.calibre_id &&
      values.item_id &&
      noCalibreInitedFor.current !== values.item_id
    ) {
      noCalibreInitedFor.current = values.item_id;
      populateFromItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calibre_list, values.calibre_id, values.item_id]);
  // Sync the free-form Calibre Name display from the item, once per loaded watch.
  useEffect(() => {
    set_calibre_name(values.calibre_name ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.item_id]);
  useEffect(() => {
    if (reserve_days !== "") {
      set_reserve_hours(parseFloat((reserve_days * 24).toFixed(4)));
      // reserve_hours formik field is set independently via its own onChange
    }
  }, [reserve_days]);
  // EOL fields only apply to quartz movements — match by name so this doesn't
  // depend on a hardcoded movement_type_id.
  const isQuartz = !!movement_type_override
    && (calibre_movement_type_list || []).some(
      (m) => m.movement_id === movement_type_override && (m.movement || "").toLowerCase() === "quartz",
    );
  return (
    <div>
      <Accordion
        expanded={true}
        style={{
          width: "100%",
        }}
      >
        <AccordionSummary
          style={{ cursor: "default" }}
          aria-controls="panel1a-content"
          id="panel1a-header"
          tabIndex={-1}
        >
          <Typography>Movement</Typography>
          {calibreLoading && <CircularProgress size={14} thickness={5} style={{ marginLeft: 8, alignSelf: "center" }} />}
        </AccordionSummary>
        <AccordionDetails
        className={classes.grid}
        style={{
          display: "grid",
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          flexDirection="column"
          alignItems="left"
          style={{ opacity: calibreLoading ? 0.5 : 1 }}
        >
          {/* Calibre row */}
          <Box mb={1} ml={1} display="flex" alignItems="flex-end" style={{ gap: 8 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 180 }}>
              <label style={{ fontSize: 11 }}>Calibre from DB:</label>
              <select
                disabled={siteId !== 0}
                value={values.calibre_id || ""}
                onChange={(e) => {
                  const val = e.target.value
                    ? calibre_list?.find((x) => x.calibre_id === Number(e.target.value)) || null
                    : null;
                  updateCalibre(val);
                }}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.calibre_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {calibre_list
                  ?.filter((calib) => calib.brand_id === values.brand_id || calib.brand_id === null)
                  .map((i) => (
                    <option key={i.calibre_id} value={i.calibre_id}>{i.calibre}</option>
                  ))}
              </select>
            </div>
            <Box style={{ marginLeft: 2 }} display="flex" alignItems="flex-start">
              {(() => {
                const sel = values.calibre_id
                  ? calibre_list?.find((c) => c.calibre_id === values.calibre_id)
                  : null;
                return (
                  <>
                    <Tooltip title={sel ? `Edit calibre: ${sel.calibre}` : "No calibre selected"} arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!sel}
                          onClick={sel ? () => window.open(
                            `/movement-details/calibre/${sel.calibre}/${sel.calibre_id}`,
                            '_blank',
                            'width=1400,height=900'
                          ) : undefined}
                          component="button"
                          style={{ opacity: sel ? 1 : 0.3 }}
                        >
                          <OpenInNewIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* TEMP DISABLED 2026-07-22 — "Refresh from calibre" icon hidden while the
                        calibre-rollover direction is being decided. The updateCalibre logic is
                        UNTOUCHED (still used by the dropdown onChange); re-enable by uncommenting
                        this block AND the RefreshIcon import at the top of the file.
                        Behavior when live: re-syncs this watch's movement fields from the
                        calibre's CURRENT values (same as re-selecting the calibre) — live-fetches
                        the calibre, displays its values, and clears this item's snapshot overrides
                        so it re-links; Save to persist. See memory: calibre-rollover-stale-movement.

                    <Tooltip
                      title={sel
                        ? "Refresh movement data from this calibre (pulls its current values and re-links this watch — then Save)"
                        : "No calibre selected"}
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!sel || calibreLoading || siteId !== 0}
                          onClick={sel ? () => updateCalibre(sel, undefined, false) : undefined}
                          component="button"
                          style={{ opacity: sel ? 1 : 0.3 }}
                        >
                          <RefreshIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    */}
                  </>
                );
              })()}
            </Box>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 180 }}>
              <label style={{ fontSize: 11 }}>Calibre Name:</label>
              <input
                type="text"
                disabled={siteId !== 0}
                value={calibre_name}
                onChange={(e) => {
                  set_calibre_name(e.target.value);
                  setFieldValue("calibre_name", e.target.value || null);
                }}
                placeholder="For inserting calibre manually"
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : calibre_name
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 215 }}>
              <label style={{ fontSize: 11 }}>Base Calibre:</label>
              <select
                disabled={!values.calibre_id || siteId !== 0}
                value={base_calibre_state?.calibre_id || ""}
                onChange={(e) => {
                  ensureDetached();
                  const val = e.target.value
                    ? calibre_list?.find((x) => x.calibre_id === Number(e.target.value)) || null
                    : null;
                  set_base_calibre_state(val || "");
                  setFieldValue('base_calibre', val?.calibre_id ?? null);
                }}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : base_calibre_state
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {calibre_list?.map((c) => (
                  <option key={c.calibre_id} value={c.calibre_id}>{c.calibre}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 100 }}>
              <label style={{ fontSize: 11 }}>Movement Type:</label>
              <select
                disabled={siteId !== 0}
                value={movement_type_override || ""}
                onChange={(e) => {
                  ensureDetached();
                  const val = e.target.value ? Number(e.target.value) : null;
                  set_movement_type_override(val);
                  setFieldValue('movement_type_id', val);
                }}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : movement_type_override
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {calibre_movement_type_list?.map((m) => (
                  <option key={m.movement_id} value={m.movement_id}>{m.movement}</option>
                ))}
              </select>
            </div>
            {isQuartz && (
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 30, alignItems: "center" }}>
                <label style={{ fontSize: 11 }}>EOL:</label>
                <Checkbox
                  disabled={siteId !== 0}
                  checked={!!eol_state}
                  onChange={() => {
                    ensureDetached();
                    const newVal = !eol_state;
                    set_eol_state(newVal);
                    setFieldValue("eol", newVal ? 1 : 0);
                  }}
                  name="eol"
                  style={{ padding: 4 }}
                />
              </div>
            )}
            {isQuartz && (
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 155 }}>
                <label style={{ fontSize: 11 }}>EOL Notes:</label>
                <input
                  type="text"
                  disabled={siteId !== 0}
                  value={eol_notes_state}
                  onChange={(e) => {
                    ensureDetached();
                    set_eol_notes_state(e.target.value);
                    setFieldValue("eol_notes", e.target.value || null);
                  }}
                  className={
                    siteId !== 0
                      ? `${classes.gridTwoBlack} custom-select`
                      : eol_notes_state
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                />
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: isQuartz ? 180 : 380 }}>
              <label style={{ fontSize: 11 }}>Certification:</label>
              <MultiSelectChips
                disabled={siteId !== 0}
                value={certification_ids_state}
                onChange={(ids) => {
                  ensureDetached();
                  set_certification_ids_state(ids);
                  setFieldValue("item_certification_ids", ids);
                }}
                options={certification_list || []}
                getOptionValue={(c) => c.certification_id}
                getOptionLabel={(c) => c.certification}
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : certification_ids_state.length
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                style={{ width: "100%", border: "1px solid rgba(0, 0, 0, 0.23)" }}
              />
            </div>


          </Box>

          {/* Stats row */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={siteId !== 0}
                  checked={in_house}
                  onChange={() => {
                    ensureDetached();
                    const newVal = !in_house;
                    set_in_house(newVal);
                    setFieldValue('in_house', newVal ? 1 : 0);
                  }}
                  name="in_house"
                  style={{ padding: 0 }}
                />
              }
              label={<span style={{ fontSize: 11 }}>In House</span>}
              labelPlacement="top"
              style={{ margin: 0, gap: 0 }}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 30 }}>
              <label style={{ fontSize: 11 }}>{errorDialog ? "Enter valid HZ:" : "HZ:"}</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : errorDialog
                    ? classes.gridTwoRed
                    : hz
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  handleChangeHz(e);
                }}
                value={hz}
                style={{ width: "100%" }}
                multiline
                type="number"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 50 }}>
              <label style={{ fontSize: 11 }}>VPH:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : vph
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  handleChangeVph(e);
                }}
                value={vph}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 40 }}>
              <label style={{ fontSize: 11 }}>Jewels:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : jewels
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  ensureDetached();
                  set_jewels(e.target.value);
                  setFieldValue('jewels', e.target.value !== "" ? e.target.value : null);
                }}
                value={jewels}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 40 }}>
              <label style={{ fontSize: 11 }}>Comp:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : components
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  ensureDetached();
                  set_components(e.target.value);
                  setFieldValue('components', e.target.value !== "" ? e.target.value : null);
                }}
                value={components}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 40 }}>
              <label style={{ fontSize: 11 }}>Rsrv Hr:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : reserve_hours
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  ensureDetached();
                  set_reserve_hours(e.target.value);
                  setFieldValue('reserve_hours', e.target.value !== "" ? e.target.value : null);
                }}
                value={reserve_hours}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 60 }}>
              <label style={{ fontSize: 11 }}>Rsrv Days:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : reserve_days === "1"
                    ? classes.gridTwoRed
                    : reserve_days
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  ensureDetached();
                  set_reserve_days(e.target.value);
                  setFieldValue('reserve_days', e.target.value !== "" ? e.target.value : null);
                }}
                value={reserve_days}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: 705 }}>
              <label style={{ fontSize: 11 }}>Power Reserve Notes:</label>
              <MUITextField
                className={reserve_notes ? classes.gridTwoBlue : classes.gridTwo}
                onChange={(e) => {
                  ensureDetached();
                  set_reserve_notes(e.target.value);
                  setFieldValue('reserve_notes', e.target.value || null);
                }}
                value={reserve_notes}
                style={{ width: "100%" }}
                multiline
                type="text"
                disabled={siteId !== 0}
              />
            </div>
            </Box>

          <Accordion defaultExpanded={false} style={{ boxShadow: 'none', margin: '4px 8px', border: '1px solid rgba(0,0,0,0.08)' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              style={{ minHeight: 36, padding: '0 12px' }}
            >
              <Typography variant="caption" style={{ color: "rgba(0,0,0,0.7)", fontSize: "0.8rem" }}>
                Movement Notes &amp; Complications
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column', padding: '0 8px 8px' }}>
              <Box mx={1} mb={1} style={{ width: '100%' }}>
                <label style={{ fontSize: 11, color: "rgba(0,0,0,0.88)" }}>Movement Notes:</label>
                <ReactQuill
                  theme="snow"
                  value={movement_notes}
                  readOnly={siteId !== 0}
                  onChange={(content) => {
                    ensureDetached();
                    set_movement_notes(content);
                    setFieldValue('movement_notes', content || null);
                  }}
                  style={{ backgroundColor: siteId !== 0 ? "rgba(0,0,0,0.04)" : undefined }}
                />
              </Box>

              <Box m={1} style={{ width: '100%' }}>
              <Box mb={0.5}>
                <Typography variant="caption" style={{ color: "rgba(0,0,0,0.54)", fontSize: "0.75rem" }}>
                  Complications
                </Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                {[0, 1].map((col) => (
                  <Box key={col} style={{ flex: 1 }}>
                    {calibre_complication_list?.filter((_, i) => i % 2 === col).map((comp) => {
                      const id = comp.complication_id;
                      const entry = checkboxes[id];
                      const checked = !!entry?.checked;
                      const detailLines = entry?.detailLines || [];
                      return (
                        <Box
                          key={id}
                          style={{
                            boxSizing: "border-box",
                            paddingLeft: 5,
                            paddingBottom: checked ? 6 : 0,
                          }}
                        >
                      <FormControlLabel
                        style={{ marginRight: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={siteId !== 0}
                            onChange={(e) => {
                              ensureDetached();
                              const updated = { ...checkboxes };
                              if (e.target.checked) {
                                updated[id] = { complication: comp.complication, checked: true, detailLines: [""] };
                              } else {
                                delete updated[id];
                              }
                              setCheckboxes(updated);
                              syncItemComplications(updated);
                              forceUpdate();
                            }}
                          />
                        }
                        label={<Typography variant="body2">{comp.complication}</Typography>}
                      />
                      {checked && (
                        <Box pl={1.5}>
                          {detailLines.map((line, lineIdx) => (
                            <Box key={lineIdx} display="flex" alignItems="center" mb={1}>
                              <MUITextField
                                value={line}
                                multiline
                                // label={`Detail ${lineIdx + 1}`}
                                style={{ flex: 1, fontSize: "0.8rem" }}
                                size="small"
                                disabled={siteId !== 0}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  style: { 
                                    fontSize: "0.8rem", 
                                    padding: "4px 8px", // Reduced vertical padding (4px)
                                    lineHeight: "1.2"   // Tighter line spacing
                                  }
                                }}
                                inputProps={{ style: { fontSize: "0.75rem", padding: "0 1px"} }}
                                onChange={(e) => {
                                  ensureDetached();
                                  const newDetailLines = checkboxes[id].detailLines.map((l, i) =>
                                    i === lineIdx ? e.target.value : l
                                  );
                                  const updated = {
                                    ...checkboxes,
                                    [id]: { ...checkboxes[id], detailLines: newDetailLines },
                                  };
                                  setCheckboxes(updated);
                                  syncItemComplications(updated);
                                }}
                              />
                              {lineIdx === detailLines.length - 1 && (
                                <Tooltip title="Add detail line" arrow>
                                  <IconButton
                                    size="small"
                                    disabled={siteId !== 0}
                                    style={{ padding: 2 }}
                                    onClick={() => {
                                      ensureDetached();
                                      const updated = {
                                        ...checkboxes,
                                        [id]: {
                                          ...checkboxes[id],
                                          detailLines: [...checkboxes[id].detailLines, ""],
                                        },
                                      };
                                      setCheckboxes(updated);
                                      forceUpdate();
                                    }}
                                  >
                                    <AddCircleOutlineIcon style={{ fontSize: "0.95rem" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {detailLines.length > 1 && (
                                <Tooltip title="Remove line" arrow>
                                  <IconButton
                                    size="small"
                                    disabled={siteId !== 0}
                                    style={{ padding: 2 }}
                                    onClick={() => {
                                      ensureDetached();
                                      const updated = {
                                        ...checkboxes,
                                        [id]: {
                                          ...checkboxes[id],
                                          detailLines: checkboxes[id].detailLines.filter(
                                            (_, i) => i !== lineIdx
                                          ),
                                        },
                                      };
                                      setCheckboxes(updated);
                                      syncItemComplications(updated);
                                      forceUpdate();
                                    }}
                                  >
                                    <span style={{ fontSize: "1rem", lineHeight: 1, color: "rgba(0,0,0,0.54)" }}>-</span>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </AccordionDetails>
      </Accordion>
    </div>
  );
};